/**
 * Operating-hours API wrapper.
 *
 * **v2 re-point (DNKADM-0051 U2).** The owned per-location weekly schedule + date
 * exceptions, served under `/v2/admin/locations/{id}/hours*` (handoff §5.2 area,
 * §3.1). This replaces the v1 single tenant/`serviceLocationId`-query contract
 * (DNKAPI-0081); hours are now **owned** and feed availability (the v1
 * "hours-not-wired" gap is gone, §9). Bearer-only `/v2/admin/**` surface —
 * `request()` routes it (DNKADM-0049 `isV2Surface`): bearer token, NO CSRF, NO
 * cookies, the bounded 401→refresh→retry; the mutating-audit `X-Session-Mode` stamp
 * fires on the writes (`isV2AdminSurface`).
 *
 * **Endpoints (handoff §5.2 — standard REST under the owned location):**
 *  - `GET /v2/admin/locations/{id}/hours` → {@link WeeklyHours} (the 7-day schedule).
 *  - `PUT /v2/admin/locations/{id}/hours` ← `{ days: [...] }` (the WHOLE 7-day
 *    multi-shift schedule).
 *  - `GET /v2/admin/locations/{id}/hours/exceptions` → {@link HoursException}`[]`.
 *  - `PUT /v2/admin/locations/{id}/hours/exceptions` ← `{ exceptions: [...] }` (the
 *    whole exception list).
 *
 * **Weekly schedule (§5.2, family of §5.6/§5.7):** a 7-day schedule per location —
 * `dayOfWeek` 0=Sunday … 6=Saturday (the §5.6 staff/resource convention), with
 * `isAvailable` and 1..n shifts (`startTime`/`endTime`, `HH:mm`, location-local) per
 * open day. Shift validation (mirror server, see `validateHours.ts`): `start < end`,
 * no overlaps within a day, no duplicates, **overnight disallowed** (`end <= start`
 * rejected); adjacent shifts allowed.
 *
 * **Date exceptions (§5.2):** `exceptionDate` (`YYYY-MM-DD`), `isAvailable`
 * (false = closed that date), and optional exception shifts (same shift rules).
 *
 * **The retained service-locations proxy** (`getServiceLocations` /
 * `ServiceLocation` / `ConnectionStatus`) is kept below for the not-yet-migrated
 * Activity-log location qualifier (§5.19). Since DNKADM-0099 it reads the **owned
 * v2 locations** (`GET /v2/admin/locations?status=active`) — the last
 * `/calendars/**` (direct-GHL) caller is retired — so its ids are now the owned v2
 * ids, consistent with the rest of the app. The full owned v2 location list (with
 * the broader shape) lives in `src/api/locations.ts`.
 *
 * GET parsing is DEFENSIVE (mirrors `tenantSettings.ts` / `membershipSettings.ts`):
 * a missing/wrong-typed field degrades rather than throwing; `days` is always
 * normalized to the seven `dayOfWeek` 0–6 in order, a missing day filled defensively
 * (unavailable), and an unavailable day carries no shifts.
 */
import { request } from './client';
import { ApiError } from './errors';

/** The seven days of the week, `dayOfWeek` 0=Sunday … 6=Saturday (§5.6 convention). */
const DAYS_OF_WEEK = [0, 1, 2, 3, 4, 5, 6] as const;

/**
 * One shift within a day — an open interval `startTime`/`endTime` (`"HH:mm"`,
 * 24h, location-local). A day may carry 1..n shifts (e.g. a lunch split).
 */
export interface HoursShift {
  startTime: string;
  endTime: string;
}

/**
 * One day's schedule. `dayOfWeek` is 0=Sunday … 6=Saturday. When `isAvailable` is
 * `false` the day is closed and carries no shifts; otherwise `shifts` holds 1..n
 * intervals.
 */
export interface WeeklyHoursDay {
  dayOfWeek: number;
  isAvailable: boolean;
  shifts: HoursShift[];
}

/**
 * Parsed `GET …/hours`. `locationId` echoes the queried owned location (or `null`
 * if the body carries none). `days` is always the seven days, sorted by `dayOfWeek`
 * 0→6.
 */
export interface WeeklyHours {
  locationId: string | null;
  days: WeeklyHoursDay[];
}

/**
 * One date exception (§5.2): `exceptionDate` is a local `YYYY-MM-DD`; `isAvailable`
 * false = closed that date (overriding the weekly schedule). When available, an
 * optional list of `shifts` overrides the weekly hours for that date (empty = the
 * date is open but inherits — the editor surfaces explicit shifts).
 */
export interface HoursException {
  exceptionDate: string;
  isAvailable: boolean;
  shifts: HoursShift[];
}

// --- Defensive parsing helpers -------------------------------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): the owned v2
 * `locationId` echoed by the hours endpoints arrives as a number on the wire and is
 * normalized to the `string` the UI uses — a finite number → `String(n)`, a
 * non-empty string → trimmed, else null. The owned v2 location id read by
 * `parseServiceLocation` below (DNKADM-0099) is likewise a `Long` on the wire and
 * uses this same number-or-string normalizer.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A boolean from a real boolean, or null when absent/unparseable. */
function asBoolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

/** A `"HH:mm"` time string (24h), or null. Anything else degrades to null. */
function asTime(value: unknown): string | null {
  const text = asText(value);
  if (text === null) return null;
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(text) ? text : null;
}

/** A `YYYY-MM-DD` date string, or null. */
function asDate(value: unknown): string | null {
  const text = asText(value);
  if (text === null) return null;
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
}

/**
 * A `dayOfWeek` index in 0–6, or null. Accepts a number or a numeric string;
 * anything out of range or non-integer degrades to null.
 */
function asDayOfWeek(value: unknown): number | null {
  let n: number | null = null;
  if (typeof value === 'number' && Number.isInteger(value)) n = value;
  else if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isInteger(parsed)) n = parsed;
  }
  if (n === null || n < 0 || n > 6) return null;
  return n;
}

/**
 * Parse a raw shifts array into well-formed {@link HoursShift}s, dropping any entry
 * without a valid `"HH:mm"` start AND end (a malformed shift degrades rather than
 * throwing). Returns `[]` for a non-array.
 */
function parseShifts(raw: unknown): HoursShift[] {
  if (!Array.isArray(raw)) return [];
  const shifts: HoursShift[] = [];
  for (const entry of raw) {
    if (!isObject(entry)) continue;
    const startTime = asTime(entry['startTime']);
    const endTime = asTime(entry['endTime']);
    if (startTime !== null && endTime !== null) shifts.push({ startTime, endTime });
  }
  return shifts;
}

/**
 * Parse one raw day into a {@link WeeklyHoursDay} keyed by its `dayOfWeek`, or null
 * if it has no usable `dayOfWeek`. A day is unavailable when its `isAvailable` flag
 * is explicitly `false` OR it carries no valid shifts; an unavailable day carries no
 * shifts.
 */
function parseDay(raw: unknown): { dayOfWeek: number; day: WeeklyHoursDay } | null {
  if (!isObject(raw)) return null;
  const dayOfWeek = asDayOfWeek(raw['dayOfWeek']);
  if (dayOfWeek === null) return null;

  const shifts = parseShifts(raw['shifts']);
  const flag = asBoolean(raw['isAvailable']);
  // Available only when not explicitly closed AND at least one valid shift exists.
  const isAvailable = flag !== false && shifts.length > 0;
  return {
    dayOfWeek,
    day: isAvailable
      ? { dayOfWeek, isAvailable: true, shifts }
      : { dayOfWeek, isAvailable: false, shifts: [] },
  };
}

/** An unavailable-by-default day for a `dayOfWeek` the server didn't return. */
function defaultUnavailableDay(dayOfWeek: number): WeeklyHoursDay {
  return { dayOfWeek, isAvailable: false, shifts: [] };
}

/**
 * Normalize a raw `days` payload to exactly the seven days in `dayOfWeek` 0→6 order:
 * indexes parsed days by `dayOfWeek` (last wins on a duplicate) and fills any missing
 * day with an unavailable default — so the grid always renders seven well-formed rows.
 *
 * Exported (DNKADM-0315) so the per-plan access-hours client, which shares this exact
 * `DayHoursDto` wire shape, parses identically rather than redefining it.
 */
export function parseDays(raw: unknown): WeeklyHoursDay[] {
  const byDay = new Map<number, WeeklyHoursDay>();
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      const parsed = parseDay(entry);
      if (parsed !== null) byDay.set(parsed.dayOfWeek, parsed.day);
    }
  }
  return DAYS_OF_WEEK.map((d) => byDay.get(d) ?? defaultUnavailableDay(d));
}

function parseWeeklyHours(raw: unknown): WeeklyHours {
  const o = isObject(raw) ? raw : {};
  // Accept either a top-level `days` or a wrapping object that also echoes the id.
  return {
    // The owned v2 `locationId` echo arrives as a number on the wire (DNKADM-0064).
    locationId: asId(o['locationId']),
    days: parseDays(o['days']),
  };
}

/**
 * Parse one raw exception, or null without a usable `exceptionDate`. An unavailable
 * exception carries no shifts; an available one keeps its valid shifts.
 */
function parseException(raw: unknown): HoursException | null {
  if (!isObject(raw)) return null;
  const exceptionDate = asDate(raw['exceptionDate']);
  if (exceptionDate === null) return null;
  const shifts = parseShifts(raw['shifts']);
  const isAvailable = asBoolean(raw['isAvailable']) !== false;
  return {
    exceptionDate,
    isAvailable,
    shifts: isAvailable ? shifts : [],
  };
}

/** Tolerantly extract an exceptions array (bare array or `{ exceptions: [...] }`). */
function extractExceptions(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (isObject(body) && Array.isArray(body['exceptions'])) return body['exceptions'];
  return [];
}

// --- Serialization helpers -----------------------------------------------------

/**
 * Serialize a day for the PUT body: an unavailable day carries no shifts. Exported
 * (DNKADM-0315) for the per-plan access-hours client, which PUTs the same shape.
 */
export function dayToBody(day: WeeklyHoursDay): Record<string, unknown> {
  return day.isAvailable
    ? {
        dayOfWeek: day.dayOfWeek,
        isAvailable: true,
        shifts: day.shifts.map((s) => ({ startTime: s.startTime, endTime: s.endTime })),
      }
    : { dayOfWeek: day.dayOfWeek, isAvailable: false, shifts: [] };
}

/** Serialize an exception for the PUT body. */
function exceptionToBody(ex: HoursException): Record<string, unknown> {
  return {
    exceptionDate: ex.exceptionDate,
    isAvailable: ex.isAvailable,
    shifts: ex.isAvailable
      ? ex.shifts.map((s) => ({ startTime: s.startTime, endTime: s.endTime }))
      : [],
  };
}

/** v2 admin owned-locations base path (bearer surface). */
const LOCATIONS_PATH = '/v2/admin/locations';

/** The `…/hours` path for an owned location id. */
function hoursPath(locationId: string): string {
  return `${LOCATIONS_PATH}/${encodeURIComponent(locationId)}/hours`;
}

// --- Endpoints (v2 per-location hours, DNKADM-0051 U2) -------------------------

/**
 * `GET /v2/admin/locations/{id}/hours` → the location's owned weekly schedule.
 * Defensively parsed (always seven days in `dayOfWeek` 0→6 order; a missing day
 * fills unavailable). Throws a typed `ApiError` on a non-2xx response.
 */
export async function getOperatingHours(locationId: string): Promise<WeeklyHours> {
  const body = await request<unknown>('GET', hoursPath(locationId));
  return parseWeeklyHours(body);
}

/**
 * `PUT /v2/admin/locations/{id}/hours` — set the WHOLE 7-day multi-shift schedule
 * for the owned location. Sends all seven days (an unavailable day with `shifts: []`,
 * an available day with its 1..n shifts). Client validation should run first
 * (`validateHours.ts`). Bearer-only (no CSRF, no cookies). Throws a typed `ApiError`:
 * `400` VALIDATION (bad shift / overlap), `403` FORBIDDEN, upstream forwarded.
 * Refetch after this resolves.
 */
export async function putOperatingHours(
  days: WeeklyHoursDay[],
  locationId: string,
): Promise<void> {
  await request<unknown>('PUT', hoursPath(locationId), {
    json: { days: days.map(dayToBody) },
  });
}

/**
 * `GET /v2/admin/locations/{id}/hours/exceptions` → the location's date exceptions
 * (defensively parsed; a bad entry is dropped). Throws a typed `ApiError`.
 */
export async function getHoursExceptions(locationId: string): Promise<HoursException[]> {
  const body = await request<unknown>('GET', `${hoursPath(locationId)}/exceptions`);
  return extractExceptions(body)
    .map(parseException)
    .filter((e): e is HoursException => e !== null);
}

/**
 * `PUT /v2/admin/locations/{id}/hours/exceptions` — replace the WHOLE exceptions
 * list. Client validation should run first (same shift rules). Throws a typed
 * `ApiError`. Refetch after this resolves.
 */
export async function putHoursExceptions(
  exceptions: HoursException[],
  locationId: string,
): Promise<void> {
  await request<unknown>('PUT', `${hoursPath(locationId)}/exceptions`, {
    json: { exceptions: exceptions.map(exceptionToBody) },
  });
}

// ===========================================================================
// Service-locations proxy — RETAINED for the not-yet-migrated Activity-log
// location qualifier (§5.19). Since DNKADM-0099 it reads the OWNED v2 locations
// (`GET /v2/admin/locations?status=active`), retiring the last `/calendars/**`
// caller; its ids are now the owned v2 ids. The full owned v2 location list is
// `src/api/locations.ts` (DNKADM-0051 U1) — prefer it for new v2 work.
// ===========================================================================

/**
 * The five known `connectionStatus.overall` values (DNKADM-0043). Drives the
 * chooser's health-dot color. An unrecognized wire value parses to `null`
 * ("unknown/none") rather than throwing — see {@link parseConnectionOverall}.
 */
type ConnectionOverall =
  | 'healthy'
  | 'degraded'
  | 'down'
  | 'unconfigured'
  | 'not_activated';

/**
 * One location's payment-connection health (DNKADM-0043), parsed additively and
 * DEFENSIVELY from the legacy service-location endpoint. The whole object is absent
 * on older responses / locations that don't carry it yet → `connectionStatus: null`
 * (the chooser then renders NO indicator). Every field is present-only: an unknown
 * `overall` → `null`; missing `validation`/`live` are simply omitted.
 *
 * Only the NON-SECRET fields the chooser tooltip needs are parsed; anything
 * unrecognized is ignored (never surfaced). `validation.detail` (e.g. an echoed
 * merchant id) and `live.error` (e.g. `"UNAUTHORIZED"`) are the only free-text
 * fields shown, and the contract guarantees both are non-secret.
 */
export interface ConnectionStatus {
  /** The single field that drives the indicator color; `null` when unknown/absent. */
  overall: ConnectionOverall | null;
  /** Backend-authored human copy, safe to render as-is; `null` when absent. */
  label: string | null;
  /** Stored last "Test connection" result (no live call); omitted when absent. */
  validation?: {
    status: 'unvalidated' | 'valid' | 'invalid' | null;
    lastValidatedAt: string | null;
    detail: string | null;
  };
  /** Fresh provider ping this request (may have been skipped); omitted when absent. */
  live?: {
    checked: boolean | null;
    reachable: boolean | null;
    credentialsOk: boolean | null;
    error: string | null;
  };
}

/**
 * One service location for the retained v1-era selectors. Since DNKADM-0099 `id`
 * is the **owned v2 location id** (a `Long` on the wire, normalized via {@link asId}),
 * consistent with the rest of the app; `name` is a human label (null when the
 * payload carries none). `isActive` is surfaced additively; `connectionStatus` is
 * surfaced additively for the DNKADM-0043 chooser health dot (null when the payload
 * carries no status object). `timezone` is the location's IANA zone (§4.5) — carried
 * on this selector shape (DNKADM-0210 U2) so a location-scoped screen can position on
 * the VENUE clock; null when the payload carries none.
 */
export interface ServiceLocation {
  id: string;
  name: string | null;
  isActive: boolean | null;
  connectionStatus: ConnectionStatus | null;
  /** IANA timezone (e.g. `America/Chicago`), or null when the payload carries none. */
  timezone: string | null;
}

/**
 * Extract the service-locations array TOLERANTLY from a successful body: a bare
 * array, or wrapped under a `serviceLocations`/`locations` key.
 */
function extractServiceLocations(body: unknown): unknown[] | null {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['serviceLocations'])) return body['serviceLocations'];
    if (Array.isArray(body['locations'])) return body['locations'];
  }
  return null;
}

/** The five known `overall` values; anything else degrades to null (unknown/none). */
const CONNECTION_OVERALL_VALUES = new Set<ConnectionOverall>([
  'healthy',
  'degraded',
  'down',
  'unconfigured',
  'not_activated',
]);

/** A recognized `ConnectionOverall`, or null for an unknown/absent value (never throws). */
function parseConnectionOverall(value: unknown): ConnectionOverall | null {
  return typeof value === 'string' && CONNECTION_OVERALL_VALUES.has(value as ConnectionOverall)
    ? (value as ConnectionOverall)
    : null;
}

/** A `validation.status` enum value, or null when absent/unrecognized. */
function parseValidationStatus(value: unknown): 'unvalidated' | 'valid' | 'invalid' | null {
  return value === 'unvalidated' || value === 'valid' || value === 'invalid' ? value : null;
}

/**
 * Parse the additive `connectionStatus` object DEFENSIVELY (DNKADM-0043): the whole
 * object absent / non-object → null; an unknown `overall` → null; `validation` /
 * `live` are parsed present-only and omitted when absent. Never throws.
 */
function parseConnectionStatus(raw: unknown): ConnectionStatus | null {
  if (!isObject(raw)) return null;
  const status: ConnectionStatus = {
    overall: parseConnectionOverall(raw['overall']),
    label: asText(raw['label']),
  };

  const validation = raw['validation'];
  if (isObject(validation)) {
    status.validation = {
      status: parseValidationStatus(validation['status']),
      lastValidatedAt: asText(validation['lastValidatedAt']),
      detail: asText(validation['detail']),
    };
  }

  const live = raw['live'];
  if (isObject(live)) {
    status.live = {
      checked: asBoolean(live['checked']),
      reachable: asBoolean(live['reachable']),
      credentialsOk: asBoolean(live['credentialsOk']),
      error: asText(live['error']),
    };
  }

  return status;
}

/**
 * Parse one raw service location, or null if it has no usable `id` (an entry with
 * no selectable id is dropped rather than surfaced as a half-identified option).
 */
function parseServiceLocation(raw: unknown): ServiceLocation | null {
  if (!isObject(raw)) return null;
  // The owned v2 location id is a `Long` (number on the wire) — normalize via
  // `asId` so a numeric id isn't dropped (DNKADM-0099).
  const id = asId(raw['id']);
  if (id === null) return null;
  return {
    id,
    name: asText(raw['name']),
    isActive: asBoolean(raw['isActive']),
    connectionStatus: parseConnectionStatus(raw['connectionStatus']),
    timezone: asText(raw['timezone']),
  };
}

/**
 * `GET /v2/admin/locations?status=active` → the tenant's owned (live) locations,
 * mapped to the `{ id, name, … }` selector shape. Since DNKADM-0099 this reads the
 * v2 owned-locations list (retiring the last `/calendars/**` direct-GHL caller), so
 * the ids are the owned v2 ids — consistent with the rest of the app and with the
 * v2 Activity-audit feed's `locationId` scope. Parsed defensively (a bad entry
 * dropped), but **FAIL-CLOSED on the list as a whole**: a thrown `ApiError` or a
 * body that yields no array RE-THROWS rather than degrading to `[]`. Bearer-only
 * `/v2/admin/**` (no CSRF, no cookies).
 *
 * The full owned v2 location list (with the broader CRUD shape) is `listLocations()`
 * in `src/api/locations.ts`. This narrower selector view remains for the
 * not-yet-migrated Activity-log location qualifier (§5.19).
 */
export async function getServiceLocations(): Promise<ServiceLocation[]> {
  const body = await request<unknown>('GET', '/v2/admin/locations', {
    query: { status: 'active' },
  });
  const list = extractServiceLocations(body);
  // Fail-closed: a non-array success is a contract failure, not "empty + complete".
  if (list === null) {
    throw new ApiError('UNKNOWN', 200, {
      details: { message: 'Service locations response was not a list' },
    });
  }
  return list
    .map(parseServiceLocation)
    .filter((l): l is ServiceLocation => l !== null);
}
